import { config } from '@/lib/config';
import { request } from '@/lib/api/client';
import type {
  AnalysisTask,
  DirectionCatalogItem,
  ExperimentDetail,
  GuestExperimentResponse,
  GuestSurveyResponse,
  ImportParseResult,
  QuestionConfig,
  QuickSurveyDelivered,
  QuickSurveyDraft,
  QuickSurveyTarget,
  ResearchToolsSummary,
  SurveyAnalytics,
  SurveyDetail,
  SurveyMode,
  SurveyStatus,
} from './types';

export const researchToolsApi = {
  getCatalog: () =>
    request<DirectionCatalogItem[]>('/research-tools/catalog', {
      channel: 'platform',
    }),

  getSummary: () =>
    request<ResearchToolsSummary>('/research-tools/summary', {
      channel: 'platform',
    }),

  parseImport: (payload: { raw_text?: string; filename?: string; file_bytes_base64?: string }) =>
    request<ImportParseResult>('/research-tools/surveys/import-parse', {
      method: 'POST',
      body: payload,
      channel: 'platform',
    }),

  createSurvey: (payload: {
    title: string;
    description?: string;
    audience: string;
    language: string;
    mode: SurveyMode;
    questions: QuestionConfig[];
  }) =>
    request<SurveyDetail>('/research-tools/surveys', {
      method: 'POST',
      body: payload,
      channel: 'platform',
    }),

  getSurveys: (status?: string, search?: string) =>
    request<SurveyDetail[]>('/research-tools/surveys', {
      channel: 'platform',
      query: { status, search },
    }),

  getSurvey: (surveyId: string) =>
    request<SurveyDetail>(`/research-tools/surveys/${surveyId}`, {
      channel: 'platform',
    }),

  updateSurvey: (
    surveyId: string,
    payload: {
      title?: string;
      description?: string;
      audience?: string;
      language?: string;
      mode?: SurveyMode;
      status?: SurveyStatus;
      questions?: QuestionConfig[];
    }
  ) =>
    request<SurveyDetail>(`/research-tools/surveys/${surveyId}`, {
      method: 'PUT',
      body: payload,
      channel: 'platform',
    }),

  approveSurvey: (surveyId: string) =>
    request<SurveyDetail>(`/research-tools/surveys/${surveyId}/approve`, {
      method: 'POST',
      channel: 'platform',
    }),

  deleteSurvey: (surveyId: string) =>
    request<{ status: string }>(`/research-tools/surveys/${surveyId}`, {
      method: 'DELETE',
      channel: 'platform',
    }),

  getSurveyAnalytics: (surveyId: string) =>
    request<SurveyAnalytics>(`/research-tools/surveys/${surveyId}/analytics`, {
      channel: 'platform',
    }),

  getExportSurveyCsvUrl: (surveyId: string) =>
    `${config.apiBaseUrl}/research-tools/surveys/${surveyId}/export-csv`,

  createExperiment: (payload: {
    title: string;
    experiment_type: string;
    description?: string;
    stimuli_questions: string[];
    status?: string;
  }) =>
    request<ExperimentDetail>('/research-tools/experiments', {
      method: 'POST',
      body: payload,
      channel: 'platform',
    }),

  getExperiments: (status?: string) =>
    request<ExperimentDetail[]>('/research-tools/experiments', {
      channel: 'platform',
      query: { status },
    }),

  getExperiment: (expId: string) =>
    request<ExperimentDetail>(`/research-tools/experiments/${expId}`, {
      channel: 'platform',
    }),

  updateExperimentStatus: (expId: string, status: string) =>
    request<ExperimentDetail>(`/research-tools/experiments/${expId}/status`, {
      method: 'PUT',
      body: { status },
      channel: 'platform',
    }),

  createAnalysisTask: (payload: {
    direction_id: number;
    direction_name: string;
    tool_name: string;
    files: { name: string; size: number }[];
    goal: string;
    result_format: string;
    editable_sources: string;
  }) =>
    request<AnalysisTask>('/research-tools/analysis', {
      method: 'POST',
      body: payload,
      channel: 'platform',
    }),

  getAnalysisTasks: () =>
    request<AnalysisTask[]>('/research-tools/analysis', {
      channel: 'platform',
    }),

  createQuickSurveyDraft: (command: string) =>
    request<QuickSurveyDraft>('/research-tools/quick-survey/draft', {
      method: 'POST',
      body: { command },
      channel: 'platform',
    }),

  approveAndDeliverQuickSurvey: (payload: {
    command: string;
    extracted_target: QuickSurveyTarget;
    questions: QuestionConfig[];
  }) =>
    request<QuickSurveyDelivered>('/research-tools/quick-survey/approve-deliver', {
      method: 'POST',
      body: payload,
      channel: 'platform',
    }),

  getGuestSurvey: (token: string) =>
    request<GuestSurveyResponse>(`/research-tools/public/survey/${token}`, {
      channel: 'none',
    }),

  verifyGuestCode: (token: string, email: string) =>
    request<{
      email: string;
      verified: boolean;
      verification_token: string;
      detail?: string;
      dev_code?: string | null;
    }>(`/research-tools/public/survey/${token}/verify-code`, {
      method: 'POST',
      body: { email },
      channel: 'none',
    }),

  confirmGuestCode: (token: string, email: string, code: string) =>
    request<{ email: string; verified: boolean; verification_token: string }>(
      `/research-tools/public/survey/${token}/confirm-code`,
      {
        method: 'POST',
        body: { email, code },
        channel: 'none',
      }
    ),

  submitGuestSurvey: (
    token: string,
    payload: {
      answers: Record<string, unknown>;
      is_verified?: boolean;
      verified_email?: string;
      verification_token?: string;
    }
  ) =>
    request<{ success: boolean; message: string; survey_id: string; submitted_at: string }>(
      `/research-tools/public/survey/${token}/submit`,
      {
        method: 'POST',
        body: payload,
        channel: 'none',
      }
    ),

  getGuestExperiment: (token: string) =>
    request<GuestExperimentResponse>(`/research-tools/public/experiment/${token}`, {
      channel: 'none',
    }),

  submitGuestExperiment: (
    token: string,
    payload: {
      answers: Record<string, unknown>;
      respondent_name?: string;
      respondent_email?: string;
    }
  ) =>
    request<{ success: boolean; message: string; survey_id: string; submitted_at: string }>(
      `/research-tools/public/experiment/${token}/submit`,
      {
        method: 'POST',
        body: payload,
        channel: 'none',
      }
    ),
};
